import { Providers, GetCurrenciesReturn } from '@web/walter-lib/api'
import { Method } from '~components/BuyWidget/hooks/useData'
import { getIsApplePaymentsAvailable } from '~utils/getIsApplePaymentsAvailable'

/**
 * Target object
 */
export interface TargetObject {
    [targetCode: string]:
        | {
              displayName: string
          }
        | undefined
}

/**
 * CommonLimit
 */
export interface Limit {
    /**
     * Provider id
     */
    providerId: string
    /**
     * Method id
     */
    methodId: string
    network?: string
    /**
     * Min value
     */
    min: string
    /**
     * Max value
     */
    max: string
}

/**
 * Source object
 */
export interface SourceObject {
    [sourceCode: string]:
        | {
              displayName: string
              targetCurrencies: {
                  [targetCode: string]:
                      | {
                            displayName: string
                            limits: Limit[]
                        }
                      | undefined
              }
              providers: {
                  [providerId: string]:
                      | {
                            providerName: string
                            methods: {
                                [methodId: string]: Method | undefined
                            }
                        }
                      | undefined
              }
          }
        | undefined
}

const APPLE_PAY_NAME = 'Apple Pay'
const IS_APPLE_PAYMENTS_AVAILABLE = getIsApplePaymentsAvailable()

/* eslint-disable complexity, max-lines-per-function */
export const processProvidersInfo = (providers?: Providers, currencies?: GetCurrenciesReturn) => {
    const sourceObj: SourceObject = {}
    const targetObj: TargetObject = {}

    providers?.forEach(provider => {
        provider?.methods?.forEach(method => {
            if (method.name === APPLE_PAY_NAME && !IS_APPLE_PAYMENTS_AVAILABLE) {
                return
            }

            method?.supported_source?.forEach(sourceCurrency => {
                const sourceCurrencyCode = sourceCurrency?.code || ''
                const providerId = String(provider?.provider_id) || ''
                const methodId = String(method?.id) || ''

                if (!sourceObj[sourceCurrencyCode]) {
                    sourceObj[sourceCurrencyCode] = {
                        displayName: sourceCurrency?.display_name || '',
                        targetCurrencies: {},
                        providers: {},
                    }
                }

                const sourceCurrencyObject = sourceObj[sourceCurrencyCode]
                if (!sourceCurrencyObject) {
                    return
                }

                if (sourceCurrencyObject.displayName < sourceCurrency?.display_name) {
                    sourceCurrencyObject.displayName = sourceCurrency?.display_name || ''
                }

                if (!sourceCurrencyObject.providers[providerId]) {
                    sourceCurrencyObject.providers[providerId] = {
                        providerName: provider.provider_name,
                        methods: {},
                    }
                }

                const sourceProviderObject = sourceCurrencyObject.providers[providerId]
                if (!sourceProviderObject) {
                    return
                }

                if (!sourceProviderObject.methods[methodId]) {
                    sourceProviderObject.methods[methodId] = {
                        description: method.description,
                        logoUrl: method.logo_url,
                        methodType: method.method_type,
                        name: method.name,
                        status: method.status,
                        type: method.type,
                    }
                }

                const uniqTargets = new Set((method.supported_target ?? []).map(item => item.code))

                uniqTargets.forEach(target => {
                    let targetNetwork: string | undefined
                    const list = method.supported_target.filter(item => item.code === target)
                    for (const item of list) {
                        if (item.network_code) {
                            targetNetwork = item.network_code
                            break
                        }
                    }

                    const targetCurrency = list.find(
                        item => item.code === target && item.network_code === targetNetwork
                    )

                    if (!targetCurrency) {
                        return
                    }

                    const targetCurrencyCode = targetCurrency.code || ''

                    targetObj[targetCurrencyCode] = {
                        displayName: currencies?.[targetCurrencyCode]?.full_name || targetCurrency?.display_name || '',
                    }

                    const currentLimit = method.transaction_limits.find(
                        item =>
                            item.source_code === sourceCurrencyCode &&
                            item.target_code === targetCurrencyCode &&
                            item.target_network_code === targetNetwork
                    )

                    const limit: Limit = {
                        providerId,
                        methodId,
                        network: targetNetwork,
                        min: currentLimit?.source_min || '',
                        max: currentLimit?.source_max || '',
                    }

                    if (!sourceCurrencyObject.targetCurrencies[targetCurrencyCode]) {
                        sourceCurrencyObject.targetCurrencies[targetCurrencyCode] = {
                            displayName: targetCurrency?.display_name || '',
                            limits: [],
                        }
                    }

                    const targetLimits = sourceCurrencyObject.targetCurrencies[targetCurrencyCode]?.limits
                    if (!targetLimits) {
                        return
                    }

                    targetLimits.push(limit)
                })
            })
        })
    })

    return { sourceObj, targetObj }
}
